import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ContextualAIChat from "@/components/ContextualAIChat";
import { 
  ArrowLeft, 
  TrendingUp,
  TrendingDown,
  PiggyBank,
  Building2,
  Coins,
  LineChart,
  Shield,
  Lightbulb,
  ChevronRight,
  Plus,
  Minus,
  Loader2,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  AlertCircle,
  Lock,
  Briefcase,
  BadgeCheck,
  Sparkles,
  Rocket
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvestPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [portfolio, setPortfolio] = useState({ holdings: [], total_value: 0, total_invested: 0 });
  const [availableInvestments, setAvailableInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [orderType, setOrderType] = useState("buy");
  const [orderAmount, setOrderAmount] = useState("");
  const [orderShares, setOrderShares] = useState("");
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("portfolio");
  const [accreditedDialogOpen, setAccreditedDialogOpen] = useState(false);
  const [isAccredited, setIsAccredited] = useState(false);
  const [accreditedAttestation, setAccreditedAttestation] = useState(false);
  const [verifyingAccredited, setVerifyingAccredited] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    setIsAccredited(userData.is_accredited_investor || false);
    fetchData(userData.id);
  }, [navigate]);

  const fetchData = async (userId) => {
    setLoading(true);
    try {
      const [portfolioRes, investmentsRes] = await Promise.all([
        axios.get(`${API}/investments/portfolio/${userId}`),
        axios.get(`${API}/investments/available`)
      ]);
      setPortfolio(portfolioRes.data);
      setAvailableInvestments(investmentsRes.data.investments);
    } catch (error) {
      console.error("Error fetching investment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (value) => {
    setOrderAmount(value);
    if (value && selectedInvestment) {
      const shares = (parseFloat(value) / selectedInvestment.price).toFixed(4);
      setOrderShares(shares);
    } else {
      setOrderShares("");
    }
  };

  const handleSharesChange = (value) => {
    setOrderShares(value);
    if (value && selectedInvestment) {
      const amount = (parseFloat(value) * selectedInvestment.price).toFixed(2);
      setOrderAmount(amount);
    } else {
      setOrderAmount("");
    }
  };

  const handlePlaceOrder = async () => {
    if (!orderShares || parseFloat(orderShares) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    // Check minimum investment
    const orderValue = parseFloat(orderShares) * selectedInvestment.price;
    const minInvestment = selectedInvestment.min_investment || 1;
    if (orderType === "buy" && orderValue < minInvestment) {
      toast.error(`Minimum investment is ${formatCurrency(minInvestment)}`);
      return;
    }

    // For sell orders, check if user has enough shares
    if (orderType === "sell") {
      const holding = portfolio.holdings.find(h => h.symbol === selectedInvestment.symbol);
      if (!holding || holding.shares < parseFloat(orderShares)) {
        toast.error("Insufficient shares to sell");
        return;
      }
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/investments/order`, {
        user_id: user.id,
        symbol: selectedInvestment.symbol,
        name: selectedInvestment.name,
        type: selectedInvestment.type,
        order_type: orderType,
        shares: parseFloat(orderShares),
        price: selectedInvestment.price
      });

      toast.success(response.data.message);
      setOrderDialogOpen(false);
      fetchData(user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Order failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyAccredited = async () => {
    if (!accreditedAttestation) {
      toast.error("Please confirm the attestation");
      return;
    }

    setVerifyingAccredited(true);
    try {
      const response = await axios.post(`${API}/user/accredited-status/${user.id}`, null, {
        params: { is_accredited: true, attestation: true }
      });

      if (response.data.is_accredited_investor) {
        setIsAccredited(true);
        // Update localStorage
        const updatedUser = { ...user, is_accredited_investor: true };
        localStorage.setItem("tamba_user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        toast.success("You're now verified as an accredited investor!");
        setAccreditedDialogOpen(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Verification failed");
    } finally {
      setVerifyingAccredited(false);
    }
  };

  const openOrderDialog = (investment, type = "buy") => {
    // Check if accredited investor required
    if (investment.accredited_only && !isAccredited) {
      setAccreditedDialogOpen(true);
      return;
    }
    setSelectedInvestment(investment);
    setOrderType(type);
    setOrderAmount("");
    setOrderShares("");
    setOrderDialogOpen(true);
  };

  const getInvestmentIcon = (type) => {
    switch (type) {
      case "etf": return Building2;
      case "stock": return LineChart;
      case "bond": return Shield;
      case "savings": return PiggyBank;
      case "private_credit": return Briefcase;
      case "private_equity": return Building2;
      case "venture_capital": return Rocket;
      case "angel": return Sparkles;
      default: return Coins;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const riskLevel = user?.risk_tolerance < 30 ? "Conservative" :
                    user?.risk_tolerance < 60 ? "Moderate" : "Aggressive";

  const suggestedPrompts = [
    "What investments match my risk profile?",
    "Should I invest in ETFs or individual stocks?",
    "Explain private equity vs venture capital",
    "Am I eligible for accredited investments?"
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tamba-teal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-semibold">Invest</h1>
            <p className="text-xs text-muted-foreground">Grow your wealth</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Demo Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-tamba-yellow/10 border border-tamba-yellow/30 rounded-xl p-3 flex items-center gap-3"
        >
          <AlertCircle className="h-5 w-5 text-tamba-yellow flex-shrink-0" />
          <p className="text-sm">
            <span className="font-medium">Demo Mode:</span> This is a simulated investment environment for learning. No real money is involved.
          </p>
        </motion.div>

        {/* Portfolio Summary */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-tamba-teal/30 bg-gradient-to-br from-tamba-teal/5 to-transparent">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-tamba-teal/20 flex items-center justify-center">
                  <Wallet className="h-7 w-7 text-tamba-teal" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Portfolio Value</p>
                  <p className="font-heading text-3xl font-bold">{formatCurrency(portfolio.total_value)}</p>
                  {portfolio.total_invested > 0 && (
                    <div className={`flex items-center gap-1 text-sm ${portfolio.total_gain_loss >= 0 ? "text-green-600" : "text-red-500"}`}>
                      {portfolio.total_gain_loss >= 0 ? (
                        <ArrowUpRight className="h-4 w-4" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4" />
                      )}
                      <span>
                        {formatCurrency(Math.abs(portfolio.total_gain_loss))} ({portfolio.total_gain_loss_percent?.toFixed(2)}%)
                      </span>
                    </div>
                  )}
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  riskLevel === "Conservative" ? "bg-green-100 text-green-700" :
                  riskLevel === "Moderate" ? "bg-tamba-yellow/20 text-tamba-yellow" :
                  "bg-tamba-coral/20 text-tamba-coral"
                }`}>
                  {riskLevel}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="portfolio" data-testid="portfolio-tab">My Portfolio</TabsTrigger>
            <TabsTrigger value="browse" data-testid="browse-tab">Browse Investments</TabsTrigger>
          </TabsList>

          <TabsContent value="portfolio" className="mt-4 space-y-4">
            {portfolio.holdings.length > 0 ? (
              portfolio.holdings.map((holding, index) => {
                const Icon = getInvestmentIcon(holding.type);
                return (
                  <motion.div
                    key={holding.symbol}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-border/50 hover:border-tamba-teal/30 hover:shadow-md transition-all">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                            <Icon className="h-6 w-6 text-tamba-teal" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{holding.symbol}</h3>
                              <span className="text-xs text-muted-foreground">{holding.shares.toFixed(4)} shares</span>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{holding.name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(holding.current_value)}</p>
                            <p className={`text-xs flex items-center justify-end gap-1 ${holding.gain_loss >= 0 ? "text-green-600" : "text-red-500"}`}>
                              {holding.gain_loss >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                              {holding.gain_loss_percent?.toFixed(2)}%
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            className="flex-1 rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
                            onClick={() => openOrderDialog({ ...holding, price: holding.current_price }, "buy")}
                            data-testid={`buy-more-${holding.symbol}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Buy More
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 rounded-full"
                            onClick={() => openOrderDialog({ ...holding, price: holding.current_price }, "sell")}
                            data-testid={`sell-${holding.symbol}`}
                          >
                            <Minus className="h-4 w-4 mr-1" />
                            Sell
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            ) : (
              <Card className="border-dashed border-2 border-border/50">
                <CardContent className="py-12 text-center">
                  <PiggyBank className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <h3 className="font-semibold mb-2">No Investments Yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Start building your portfolio by browsing available investments
                  </p>
                  <Button 
                    onClick={() => setActiveTab("browse")}
                    className="rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
                  >
                    Browse Investments
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="browse" className="mt-4 space-y-6">
            {/* Standard Investment Categories */}
            <div className="space-y-4">
              {["etf", "stock", "bond", "savings"].map((type) => {
                const typeInvestments = availableInvestments.filter(i => i.type === type);
                if (typeInvestments.length === 0) return null;
                
                const typeLabels = {
                  etf: { label: "ETFs", desc: "Diversified funds", color: "text-tamba-blue" },
                  stock: { label: "Stocks", desc: "Individual companies", color: "text-purple-500" },
                  bond: { label: "Bonds", desc: "Fixed income", color: "text-tamba-teal" },
                  savings: { label: "Savings", desc: "High-yield accounts", color: "text-green-600" }
                };
                
                return (
                  <div key={type}>
                    <h3 className={`font-semibold mb-3 ${typeLabels[type].color}`}>
                      {typeLabels[type].label}
                      <span className="text-xs font-normal text-muted-foreground ml-2">{typeLabels[type].desc}</span>
                    </h3>
                    <div className="space-y-2">
                      {typeInvestments.map((investment) => {
                        const Icon = getInvestmentIcon(investment.type);
                        const userHolding = portfolio.holdings.find(h => h.symbol === investment.symbol);
                        
                        return (
                          <Card key={investment.symbol} className="border-border/50 hover:border-tamba-teal/30 hover:shadow-md transition-all">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                                  <Icon className="h-5 w-5 text-tamba-teal" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold">{investment.symbol}</h4>
                                    {userHolding && (
                                      <span className="text-[10px] px-2 py-0.5 bg-tamba-teal/10 text-tamba-teal rounded-full">
                                        Owned
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate">{investment.name}</p>
                                </div>
                                <div className="text-right mr-2">
                                  <p className="font-semibold">{formatCurrency(investment.price)}</p>
                                  {investment.apy ? (
                                    <p className="text-xs text-green-600">{investment.apy}% APY</p>
                                  ) : (
                                    <p className={`text-xs ${investment.change >= 0 ? "text-green-600" : "text-red-500"}`}>
                                      {investment.change >= 0 ? "+" : ""}{investment.change}%
                                    </p>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  className="rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
                                  onClick={() => openOrderDialog(investment, "buy")}
                                  data-testid={`invest-${investment.symbol}`}
                                >
                                  Invest
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Private Investments Section (Accredited Only) */}
            <div className="pt-4 border-t border-border/50">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                    <Lock className="h-5 w-5 text-amber-500" />
                    Private Investments
                  </h3>
                  <p className="text-xs text-muted-foreground">Accredited investors only • Higher minimums • Illiquid</p>
                </div>
                {!isAccredited && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full border-amber-500 text-amber-600 hover:bg-amber-50"
                    onClick={() => setAccreditedDialogOpen(true)}
                    data-testid="verify-accredited-btn"
                  >
                    <BadgeCheck className="h-4 w-4 mr-1" />
                    Verify Status
                  </Button>
                )}
                {isAccredited && (
                  <span className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                    <BadgeCheck className="h-3 w-3" />
                    Verified
                  </span>
                )}
              </div>

              {["private_credit", "private_equity", "venture_capital", "angel"].map((type) => {
                const typeInvestments = availableInvestments.filter(i => i.type === type);
                if (typeInvestments.length === 0) return null;
                
                const typeLabels = {
                  private_credit: { label: "Private Credit", desc: "Direct lending", color: "text-amber-600", icon: Briefcase },
                  private_equity: { label: "Private Equity", desc: "Company ownership", color: "text-indigo-600", icon: Building2 },
                  venture_capital: { label: "Venture Capital", desc: "Startup investing", color: "text-pink-600", icon: Rocket },
                  angel: { label: "Angel Investing", desc: "Early-stage syndicate", color: "text-cyan-600", icon: Sparkles }
                };
                
                const TypeIcon = typeLabels[type].icon;
                
                return (
                  <div key={type} className="mb-4">
                    <h4 className={`font-medium mb-2 flex items-center gap-2 ${typeLabels[type].color}`}>
                      <TypeIcon className="h-4 w-4" />
                      {typeLabels[type].label}
                      <span className="text-xs font-normal text-muted-foreground">{typeLabels[type].desc}</span>
                    </h4>
                    <div className="space-y-2">
                      {typeInvestments.map((investment) => (
                        <Card 
                          key={investment.symbol} 
                          className={`border-border/50 transition-all ${
                            isAccredited 
                              ? "hover:border-amber-500/30 hover:shadow-md" 
                              : "opacity-75"
                          }`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                isAccredited ? "bg-amber-100" : "bg-secondary"
                              }`}>
                                <TypeIcon className={`h-5 w-5 ${isAccredited ? typeLabels[type].color : "text-muted-foreground"}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-sm">{investment.name}</h4>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {investment.term} • {investment.risk_level} Risk
                                </p>
                              </div>
                              <div className="text-right mr-2">
                                <p className="font-semibold text-green-600">{investment.target_return}% Target</p>
                                <p className="text-xs text-muted-foreground">
                                  Min: {formatCurrency(investment.min_investment)}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                className={`rounded-full ${
                                  isAccredited 
                                    ? "bg-amber-500 hover:bg-amber-600" 
                                    : "bg-secondary text-muted-foreground"
                                }`}
                                onClick={() => openOrderDialog(investment, "buy")}
                                disabled={!isAccredited}
                                data-testid={`invest-${investment.symbol}`}
                              >
                                {isAccredited ? "Invest" : <Lock className="h-4 w-4" />}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Investment Tips */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-tamba-yellow/30 bg-gradient-to-br from-tamba-yellow/5 to-transparent">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-tamba-yellow" />
                Investment Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm">
                  <span className="text-tamba-yellow mt-0.5">•</span>
                  <span>Start with your employer's 401(k) match - it's free money</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <span className="text-tamba-yellow mt-0.5">•</span>
                  <span>Build a 3-6 month emergency fund before investing</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <span className="text-tamba-yellow mt-0.5">•</span>
                  <span>Diversify across different asset classes to reduce risk</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {orderType === "buy" ? (
                <Plus className="h-5 w-5 text-tamba-teal" />
              ) : (
                <Minus className="h-5 w-5 text-tamba-coral" />
              )}
              {orderType === "buy" ? "Buy" : "Sell"} {selectedInvestment?.symbol}
            </DialogTitle>
            <DialogDescription>
              {selectedInvestment?.name} • {formatCurrency(selectedInvestment?.price || 0)} per share
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {orderType === "sell" && (
              <div className="p-3 bg-secondary/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  You own: <span className="font-medium text-foreground">
                    {portfolio.holdings.find(h => h.symbol === selectedInvestment?.symbol)?.shares.toFixed(4) || 0} shares
                  </span>
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Amount (USD)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="0.00"
                  value={orderAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="pl-9"
                  data-testid="order-amount-input"
                />
              </div>
            </div>

            <div className="text-center text-muted-foreground text-sm">or</div>

            <div>
              <label className="text-sm font-medium mb-2 block">Number of Shares</label>
              <Input
                type="number"
                placeholder="0.0000"
                value={orderShares}
                onChange={(e) => handleSharesChange(e.target.value)}
                data-testid="order-shares-input"
              />
            </div>

            {orderAmount && orderShares && (
              <div className="p-4 bg-secondary/50 rounded-xl">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Price per share</span>
                  <span>{formatCurrency(selectedInvestment?.price || 0)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Shares</span>
                  <span>{parseFloat(orderShares).toFixed(4)}</span>
                </div>
                <div className="border-t border-border pt-2 mt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span className={orderType === "sell" ? "text-green-600" : ""}>
                    {orderType === "sell" ? "+" : ""}{formatCurrency(parseFloat(orderAmount) || 0)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePlaceOrder}
              disabled={processing || !orderShares || parseFloat(orderShares) <= 0}
              className={`rounded-full ${orderType === "buy" ? "bg-tamba-teal hover:bg-tamba-teal/90" : "bg-tamba-coral hover:bg-tamba-coral/90"}`}
              data-testid="confirm-order-btn"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {orderType === "buy" ? "Buy" : "Sell"} {selectedInvestment?.symbol}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accredited Investor Dialog */}
      <Dialog open={accreditedDialogOpen} onOpenChange={setAccreditedDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-amber-500" />
              Accredited Investor Verification
            </DialogTitle>
            <DialogDescription>
              Private investments require accredited investor status under SEC regulations
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-medium text-amber-800 mb-2">Who qualifies?</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Annual income exceeding $200,000 (individual) or $300,000 (joint)</li>
                <li>• Net worth exceeding $1 million (excluding primary residence)</li>
                <li>• Licensed financial professional (Series 7, 65, or 82)</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg">
                <Checkbox
                  id="accredited-attestation"
                  checked={accreditedAttestation}
                  onCheckedChange={setAccreditedAttestation}
                  data-testid="accredited-checkbox"
                />
                <label htmlFor="accredited-attestation" className="text-sm leading-relaxed cursor-pointer">
                  I certify that I am an "accredited investor" as defined by SEC Rule 501 of Regulation D. 
                  I understand that private investments are illiquid, high-risk, and I may lose my entire investment.
                </label>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              By verifying, you confirm you meet at least one qualification criteria. 
              Tamba may request documentation to verify your status.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAccreditedDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleVerifyAccredited}
              disabled={verifyingAccredited || !accreditedAttestation}
              className="rounded-full bg-amber-500 hover:bg-amber-600"
              data-testid="verify-accredited-submit"
            >
              {verifyingAccredited ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Verify My Status"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contextual AI Chat */}
      {user && (
        <ContextualAIChat
          context="invest"
          userId={user.id}
          pageData={{ 
            risk_tolerance: user.risk_tolerance,
            portfolio_value: portfolio.total_value,
            holdings_count: portfolio.holdings.length
          }}
          suggestedPrompts={suggestedPrompts}
          accentColor="tamba-teal"
        />
      )}
    </div>
  );
};

export default InvestPage;
